import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import DOMPurify from "dompurify";
import { Button } from "../components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { 
  ArrowLeft, Send, Trash2, Settings, Plus, Copy, Bot, User, Sparkles, RotateCcw, Pencil, Star, SlidersHorizontal, RefreshCw
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import ChatInput from "../components/ChatInput";
import MessageContent, { renderTextWithBold } from "../components/MessageContent";

// Available models
const MODELS = [
  { provider: "openai", name: "gpt-5.2", label: "GPT-5.2 (OpenAI)" },
  { provider: "openai", name: "gpt-4o-mini", label: "GPT-4o-mini (OpenAI)" },
  { provider: "anthropic", name: "claude-opus-4-6", label: "Claude Opus 4.6 (Anthropic)" },
  { provider: "anthropic", name: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Anthropic)" },
  { provider: "anthropic", name: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (Anthropic)" },
  { provider: "gemini", name: "gemini-2.0-flash", label: "Gemini 2.0 Flash (Google)" },
  { provider: "gemini", name: "gemini-3-flash-preview", label: "Gemini 3 Flash Preview (Google)" },
  { provider: "groq", name: "llama-3.3-70b-versatile", label: "Llama 3.3 70B (Groq)" },
  { provider: "groq", name: "llama-3.1-8b-instant", label: "Llama 3.1 8B Instant (Groq)" },
];

// AMP Results Component - Side-by-side model comparison
const AmpResults = ({ results, prompt, isCyber }) => {
  return (
    <div className="w-full">
      <div className="mb-4 p-3 rounded-lg bg-white/5">
        <p className="text-white/60 text-sm mb-1">Prompt tested:</p>
        <p className="text-white font-medium">{prompt}</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {results.map((result, idx) => (
          <div 
            key={idx}
            className={`rounded-xl p-4 ${
              result.success 
                ? isCyber 
                  ? "bg-cyan-500/5 border border-cyan-500/20" 
                  : "bg-divine-gold/5 border border-divine-gold/20"
                : "bg-red-500/10 border border-red-500/30"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`font-semibold text-sm ${
                isCyber ? "text-cyan-400" : "text-divine-gold"
              }`}>
                {result.model}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                result.success 
                  ? "bg-green-500/20 text-green-400"
                  : "bg-red-500/20 text-red-400"
              }`}>
                {result.success ? "Success" : "Error"}
              </span>
            </div>
            <div className="text-white/80 text-sm max-h-64 overflow-y-auto">
              <MessageContent content={result.response} isCyber={isCyber} />
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(result.response);
              }}
              className={`mt-3 text-xs px-3 py-1 rounded ${
                isCyber 
                  ? "bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20" 
                  : "bg-divine-gold/10 text-divine-gold hover:bg-divine-gold/20"
              } transition-colors`}
            >
              Copy Response
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const TestChat = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";
  
  // Chat type options
  const CHAT_TYPES = [
    { value: "direct", label: "Direct", description: "Raw AI response, no coaching" },
    { value: "coaching", label: "Coaching", description: "Agent Eros coaching flow" },
    { value: "persona", label: "Persona", description: "AI roleplays as selected persona" },
    { value: "practice", label: "Practice", description: "Practice scenarios with feedback" },
    { value: "function", label: "Function", description: "Test specific AI functions" },
  ];
  
  // State
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [relationships, setRelationships] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [includeUserData, setIncludeUserData] = useState(true);  // Default ON
  const [chatType, setChatType] = useState("coaching");  // Default to coaching
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showSessionList, setShowSessionList] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [preparingContext, setPreparingContext] = useState(false);  // Loading state for CORE data
  
  // Practice mode state
  const [practiceScenarios, setPracticeScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  
  const messagesEndRef = useRef(null);
  const sendMessageRef = useRef(null);

  // Prepare CORE data when persona is selected or My Data is toggled
  const prepareContextData = async (matchId, includeUser) => {
    if (!matchId && !includeUser) return;
    
    setPreparingContext(true);
    try {
      const response = await authAxios.post("/test-chat/prepare-context", {
        match_id: matchId || null,
        include_user_data: includeUser
      });
      
      if (response.data.match_data_generated) {
        toast.success("Generated match context data");
      }
      if (response.data.user_data_generated) {
        toast.success("Generated user context data");
      }
    } catch (error) {
      console.error("Error preparing context:", error);
      // Don't show error toast - fallback will be used
    } finally {
      setPreparingContext(false);
    }
  };

  // Handler for persona selection change
  const handlePersonaChange = async (value) => {
    const newPersona = value === "none" ? null : value;
    setSelectedPersona(newPersona);
    if (newPersona) {
      await prepareContextData(newPersona, includeUserData);
    }
  };

  // Handler for My Data toggle change
  const handleUserDataToggle = async (checked) => {
    setIncludeUserData(checked);
    if (checked) {
      await prepareContextData(selectedPersona, true);
    }
  };

  // Memoize sorted scenarios to prevent re-computation on every render
  const sortedScenarios = useMemo(() => {
    return [...practiceScenarios].sort((a, b) => {
      const order = { easy: 1, medium: 2, hard: 3 };
      return (order[a.difficulty] || 2) - (order[b.difficulty] || 2);
    });
  }, [practiceScenarios]);

  useEffect(() => {
    fetchRelationships();
    fetchSessions();
    fetchPracticeScenarios();
  }, []);

  const fetchPracticeScenarios = async () => {
    try {
      const response = await authAxios.get("/v2/persona/practice/scenarios");
      setPracticeScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error("Error fetching practice scenarios:", error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchRelationships = async () => {
    try {
      const response = await authAxios.get("/matches");
      setRelationships(response.data);
    } catch (error) {
      console.error("Error fetching relationships:", error);
    }
  };

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await authAxios.get("/chat-sessions", {
        params: { context: "test_chat" }
      });
      setSessions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await authAxios.post("/chat-sessions", {
        context: "test_chat",
        match_id: selectedPersona || null,
        mode: chatType,  // Store chat type as mode (direct, coaching, persona, practice, function)
        scenario_id: chatType === "practice" ? selectedScenario : null,
        model_provider: selectedModel.provider,
        model_name: selectedModel.name
      });
      
      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(newSession.session_id);
      setMessages([]);
      setShowSessionList(false);
      toast.success("New test session started!");
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error("Failed to create session");
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await authAxios.get(`/chat-sessions/${sessionId}`);
      const { session, messages: sessionMessages } = response.data;
      
      setCurrentSessionId(sessionId);
      
      // Restore session settings
      const sessionType = session.mode || "coaching";
      setChatType(sessionType);
      setSelectedPersona(session.match_id || null);
      setSelectedScenario(session.scenario_id || null);
      
      // Restore model from session fields (new way) or custom_system_prompt (legacy)
      if (session.model_provider && session.model_name) {
        const model = MODELS.find(m => m.provider === session.model_provider && m.name === session.model_name);
        if (model) setSelectedModel(model);
      } else if (session.custom_system_prompt) {
        // Legacy: try to parse from custom_system_prompt
        try {
          const parsed = JSON.parse(session.custom_system_prompt);
          if (parsed.model_provider && parsed.model_name) {
            const model = MODELS.find(m => m.provider === parsed.model_provider && m.name === parsed.model_name);
            if (model) setSelectedModel(model);
          }
        } catch (e) { console.debug("session model_info parse failed:", e?.message || e); }
      }
      
      setMessages(sessionMessages.map(m => ({
        role: m.role,
        content: m.content,
        model: m.model_name ? `${m.model_provider}/${m.model_name}` : null
      })));
      setShowSessionList(false);
    } catch (error) {
      console.error("Error loading session:", error);
      toast.error("Failed to load session");
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!confirm("Delete this test session?")) return;
    
    try {
      await authAxios.delete(`/chat-sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
        setShowSessionList(true);
      }
      toast.success("Session deleted");
    } catch (error) {
      toast.error("Failed to delete session");
    }
  };

  const renameSession = async (sessionId, e) => {
    e.stopPropagation();
    const session = sessions.find(s => s.session_id === sessionId);
    const newTitle = prompt("Rename session:", session?.title || "");
    if (!newTitle || newTitle === session?.title) return;
    
    try {
      await authAxios.patch(`/chat-sessions/${sessionId}`, { title: newTitle });
      setSessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, title: newTitle } : s
      ));
      toast.success("Session renamed");
    } catch (error) {
      console.error("Error renaming session:", error);
      toast.error("Failed to rename session");
    }
  };

  const toggleFavourite = async (sessionId, e) => {
    e.stopPropagation();
    const session = sessions.find(s => s.session_id === sessionId);
    const newFavourite = !session?.is_favourite;
    
    try {
      await authAxios.patch(`/chat-sessions/${sessionId}`, { is_favourite: newFavourite });
      setSessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, is_favourite: newFavourite } : s
      ));
      toast.success(newFavourite ? "Added to favourites" : "Removed from favourites");
    } catch (error) {
      console.error("Error toggling favourite:", error);
      toast.error("Failed to update favourite");
    }
  };

  // Handler for the isolated ChatInput component - memoized to prevent re-renders
  const handleSendMessage = useCallback(async (inputMessage) => {
    if (!inputMessage.trim()) return;
    
    // Auto-create session if none selected
    if (!currentSessionId) {
      try {
        const response = await authAxios.post("/chat-sessions", {
          context: "test_chat",
          match_id: selectedPersona || null
        });
        
        const newSession = response.data;
        setSessions(prev => [newSession, ...prev]);
        setCurrentSessionId(newSession.session_id);
        setShowSessionList(false);
        
        await sendMessageRef.current(inputMessage, newSession.session_id);
        return;
      } catch (error) {
        toast.error("Failed to start session");
        return;
      }
    }
    
    await sendMessageRef.current(inputMessage, currentSessionId);
  }, [currentSessionId, selectedPersona]);

  const sendMessage = async (userMsg, sessionId) => {
    const isCommand = userMsg.startsWith("/");
    
    if (!isCommand) {
      setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    }
    
    setLoading(true);
    
    try {
      const response = await authAxios.post("/test-chat", {
        message: userMsg,
        session_id: sessionId,
        model_provider: selectedModel.provider,
        model_name: selectedModel.name,
        match_id: selectedPersona || null,
        include_user_data: includeUserData,
        chat_type: chatType,
        practice_mode: chatType === "practice",
        scenario_id: chatType === "practice" ? selectedScenario : null
      });
      
      const data = response.data;
      
      // Handle clear command
      if (data.clear_messages) {
        setMessages([]);
      }
      
      // Handle AMP (All Models Parallel) response
      if (data.is_amp && data.amp_results) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: data.response,
          isCommand: true,
          isAmp: true,
          ampResults: data.amp_results,
          ampPrompt: data.amp_prompt
        }]);
      }
      // Add response to messages
      else if (!data.clear_messages) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: data.response,
          model: data.model_used,
          isCommand: data.is_command,
          isError: data.is_error,
          practiceFeedback: data.practice_feedback
        }]);
      }
      
      // Refresh sessions if title might have changed
      if (!data.is_command) {
        fetchSessions();
      }
      
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "❌ Error: " + (error.response?.data?.detail || error.message),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Keep sendMessageRef updated with the latest sendMessage function
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  });

  const handleClearChat = async () => {
    if (currentSessionId) {
      await sendMessage("/clear", currentSessionId);
    }
  };

  return (
    <div className="h-screen bg-divine-black flex flex-col overflow-hidden">
      <Toaster position="top-center" />
      
      {/* Header - Fixed at top with blur */}
      <header className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]/70" : "border-divine-gold/30 bg-black/40"}`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setCurrentSessionId(null);
                  setMessages([]);
                  setShowSessionList(true);
                }}
                className={isCyber ? "text-cyan-400" : "text-divine-gold"}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className={`font-cinzel text-xl ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                  Test Chat Lab
                </h1>
                <p className="text-white/40 text-xs">Test different models and prompts</p>
              </div>
            </div>
            
            {/* Controls - Hidden on mobile, shown on md+ */}
            <div className="hidden md:flex items-center gap-3">
              {/* Chat Type Select */}
              <div className="flex items-center gap-2">
                <Sparkles className={`w-4 h-4 ${isCyber ? "text-purple-400" : "text-amber-400"}`} />
                <Select
                  value={chatType}
                  onValueChange={setChatType}
                >
                  <SelectTrigger className={`w-[130px] ${isCyber ? "border-purple-500/30 bg-[#0a0a0f]" : "border-amber-500/30 bg-divine-black"}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isCyber ? "bg-[#0a0a0f] border-purple-500/30" : "bg-divine-black border-amber-500/30"}>
                    {CHAT_TYPES.map((type) => (
                      <SelectItem 
                        key={type.value} 
                        value={type.value}
                        className="text-white"
                      >
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Model Select */}
              <div className="flex items-center gap-2">
                <Bot className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                <Select
                  value={`${selectedModel.provider}/${selectedModel.name}`}
                  onValueChange={(value) => {
                    const [provider, name] = value.split("/");
                    const model = MODELS.find(m => m.provider === provider && m.name === name);
                    if (model) setSelectedModel(model);
                  }}
                >
                  <SelectTrigger className={`w-[180px] ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"}>
                    {MODELS.map((model) => (
                      <SelectItem 
                        key={`${model.provider}/${model.name}`} 
                        value={`${model.provider}/${model.name}`}
                        className="text-white"
                      >
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Persona Select */}
              <div className="flex items-center gap-2">
                <User className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                <Select
                  value={selectedPersona || "none"}
                  onValueChange={handlePersonaChange}
                  disabled={preparingContext}
                >
                  <SelectTrigger className={`w-[140px] ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"} ${preparingContext ? "opacity-50" : ""}`}>
                    <SelectValue placeholder="No Persona" />
                  </SelectTrigger>
                  <SelectContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"}>
                    <SelectItem value="none" className="text-white">No Persona</SelectItem>
                    {relationships.map((r) => (
                      <SelectItem key={r.match_id} value={r.match_id} className="text-white">
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {preparingContext && (
                  <RefreshCw className={`w-4 h-4 animate-spin ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                )}
              </div>
              
              {/* Include User Data Toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  id="include-user-data"
                  checked={includeUserData}
                  onCheckedChange={handleUserDataToggle}
                  disabled={preparingContext}
                  className={isCyber ? "data-[state=checked]:bg-cyan-500" : "data-[state=checked]:bg-divine-gold"}
                />
                <Label htmlFor="include-user-data" className="text-white/60 text-sm cursor-pointer">
                  My Data
                </Label>
              </div>

              {/* Scenario Dropdown - Only show when chat type is Practice */}
              {chatType === "practice" && (
                <Select
                  value={selectedScenario || "none"}
                  onValueChange={(value) => setSelectedScenario(value === "none" ? null : value)}
                >
                  <SelectTrigger className="w-[180px] border-emerald-500/30 bg-emerald-500/10">
                    <SelectValue placeholder="Select Scenario" />
                  </SelectTrigger>
                  <SelectContent className={isCyber ? "bg-[#0a0a0f] border-emerald-500/30" : "bg-divine-black border-emerald-500/30"}>
                    <SelectItem value="none" className="text-white/60">No Scenario</SelectItem>
                    {sortedScenarios.map((scenario) => {
                      const diffColors = {
                        easy: "text-green-400",
                        medium: "text-yellow-400", 
                        hard: "text-red-400"
                      };
                      const diffIcons = { easy: "⚡", medium: "🌱", hard: "🔥" };
                      return (
                        <SelectItem key={scenario.scenario_id} value={scenario.scenario_id} className="text-white">
                          <span className="flex items-center gap-2">
                            <span className={diffColors[scenario.difficulty]}>{diffIcons[scenario.difficulty]}</span>
                            {scenario.name}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Mobile Settings Button - Only visible on mobile when in chat */}
            {!showSessionList && currentSessionId && (
              <Sheet open={mobileSettingsOpen} onOpenChange={setMobileSettingsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`md:hidden ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}
                    data-testid="mobile-settings-btn"
                  >
                    <SlidersHorizontal className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent 
                  side="bottom" 
                  className={`${isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"} rounded-t-2xl`}
                >
                  <SheetHeader>
                    <SheetTitle className={`${isCyber ? "text-cyan-400" : "text-divine-gold"} font-cinzel`}>
                      Chat Settings
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-4 mt-4 pb-4">
                    {/* Mode Select */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className={`w-3 h-3 ${isCyber ? "text-purple-400" : "text-amber-400"}`} />
                        <span className="text-white/60 text-sm">Mode</span>
                      </div>
                      <Select value={chatType} onValueChange={setChatType}>
                        <SelectTrigger className={`w-full ${isCyber ? "border-purple-500/30 bg-[#0a0a0f]" : "border-amber-500/30 bg-divine-black"}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isCyber ? "bg-[#0a0a0f] border-purple-500/30" : "bg-divine-black border-amber-500/30"}>
                          {CHAT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} className="text-white">
                              <div>
                                <div>{type.label}</div>
                                <div className="text-xs text-white/40">{type.description}</div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Model Select */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Bot className={`w-3 h-3 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                        <span className="text-white/60 text-sm">Model</span>
                      </div>
                      <Select
                        value={`${selectedModel.provider}/${selectedModel.name}`}
                        onValueChange={(value) => {
                          const [provider, name] = value.split("/");
                          const model = MODELS.find(m => m.provider === provider && m.name === name);
                          if (model) setSelectedModel(model);
                        }}
                      >
                        <SelectTrigger className={`w-full ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"}>
                          {MODELS.map((model) => (
                            <SelectItem key={`${model.provider}/${model.name}`} value={`${model.provider}/${model.name}`} className="text-white">
                              {model.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Persona Select */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <User className={`w-3 h-3 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                        <span className="text-white/60 text-sm">Persona</span>
                        {preparingContext && (
                          <RefreshCw className={`w-3 h-3 animate-spin ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                        )}
                      </div>
                      <Select
                        value={selectedPersona || "none"}
                        onValueChange={handlePersonaChange}
                        disabled={preparingContext}
                      >
                        <SelectTrigger className={`w-full ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"} ${preparingContext ? "opacity-50" : ""}`}>
                          <SelectValue placeholder="No Persona" />
                        </SelectTrigger>
                        <SelectContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"}>
                          <SelectItem value="none" className="text-white">No Persona</SelectItem>
                          {relationships.map((r) => (
                            <SelectItem key={r.match_id} value={r.match_id} className="text-white">
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Scenario Select - Only when Practice mode */}
                    {chatType === "practice" && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-emerald-400 text-xs">🎯</span>
                          <span className="text-white/60 text-sm">Scenario</span>
                        </div>
                        <Select
                          value={selectedScenario || "none"}
                          onValueChange={(value) => setSelectedScenario(value === "none" ? null : value)}
                        >
                          <SelectTrigger className="w-full border-emerald-500/30 bg-emerald-500/10">
                            <SelectValue placeholder="Select Scenario" />
                          </SelectTrigger>
                          <SelectContent className={isCyber ? "bg-[#0a0a0f] border-emerald-500/30" : "bg-divine-black border-emerald-500/30"}>
                            <SelectItem value="none" className="text-white/60">No Scenario</SelectItem>
                            {sortedScenarios.map((scenario) => {
                              const diffColors = { easy: "text-green-400", medium: "text-yellow-400", hard: "text-red-400" };
                              const diffIcons = { easy: "⚡", medium: "🌱", hard: "🔥" };
                              return (
                                <SelectItem key={scenario.scenario_id} value={scenario.scenario_id} className="text-white">
                                  <span className="flex items-center gap-2">
                                    <span className={diffColors[scenario.difficulty]}>{diffIcons[scenario.difficulty]}</span>
                                    {scenario.name}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* My Data Toggle */}
                    <div className={`flex items-center justify-between px-3 py-3 rounded-lg border ${isCyber ? "border-cyan-500/30 bg-cyan-500/5" : "border-divine-gold/30 bg-divine-gold/5"}`}>
                      <div className="flex items-center gap-2">
                        <User className={`w-4 h-4 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                        <Label htmlFor="include-user-data-sheet" className="text-white text-sm cursor-pointer">
                          Include My Data
                        </Label>
                      </div>
                      <Switch
                        id="include-user-data-sheet"
                        checked={includeUserData}
                        onCheckedChange={handleUserDataToggle}
                        disabled={preparingContext}
                        className={isCyber ? "data-[state=checked]:bg-cyan-500" : "data-[state=checked]:bg-divine-gold"}
                      />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </header>

      {/* Session Selection or Chat */}
      {showSessionList && !currentSessionId ? (
        <div className="flex-1 overflow-y-auto pt-20">
          <div className="max-w-2xl mx-auto px-6 py-8">
            <div className="text-center mb-8">
              <h2 className={`font-cinzel text-2xl mb-2 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                Test Chat Sessions
              </h2>
              <p className="text-white/60">
                Test different models, prompts, and contexts
              </p>
            </div>

            {/* Mobile Controls - Visible only on mobile */}
            <div className="flex md:hidden flex-col gap-3 mb-6">
              {/* Row 1: Chat Type & Model */}
              <div className="flex gap-3">
                {/* Chat Type Select */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className={`w-3 h-3 ${isCyber ? "text-purple-400" : "text-amber-400"}`} />
                    <span className="text-white/40 text-xs">Mode</span>
                  </div>
                  <Select value={chatType} onValueChange={setChatType}>
                    <SelectTrigger className={`w-full ${isCyber ? "border-purple-500/30 bg-[#0a0a0f]" : "border-amber-500/30 bg-divine-black"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isCyber ? "bg-[#0a0a0f] border-purple-500/30" : "bg-divine-black border-amber-500/30"}>
                      {CHAT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Model Select */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Bot className={`w-3 h-3 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                    <span className="text-white/40 text-xs">Model</span>
                  </div>
                  <Select
                    value={`${selectedModel.provider}/${selectedModel.name}`}
                    onValueChange={(value) => {
                      const [provider, name] = value.split("/");
                      const model = MODELS.find(m => m.provider === provider && m.name === name);
                      if (model) setSelectedModel(model);
                    }}
                  >
                    <SelectTrigger className={`w-full ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"}>
                      {MODELS.map((model) => (
                        <SelectItem key={`${model.provider}/${model.name}`} value={`${model.provider}/${model.name}`} className="text-white">
                          {model.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Row 2: Persona & My Data Toggle */}
              <div className="flex gap-3 items-end">
                {/* Persona Select */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User className={`w-3 h-3 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                    <span className="text-white/40 text-xs">Persona</span>
                    {preparingContext && (
                      <RefreshCw className={`w-3 h-3 animate-spin ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                    )}
                  </div>
                  <Select
                    value={selectedPersona || "none"}
                    onValueChange={handlePersonaChange}
                    disabled={preparingContext}
                  >
                    <SelectTrigger className={`w-full ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"} ${preparingContext ? "opacity-50" : ""}`}>
                      <SelectValue placeholder="No Persona" />
                    </SelectTrigger>
                    <SelectContent className={isCyber ? "bg-[#0a0a0f] border-cyan-500/30" : "bg-divine-black border-divine-gold/30"}>
                      <SelectItem value="none" className="text-white">No Persona</SelectItem>
                      {relationships.map((r) => (
                        <SelectItem key={r.match_id} value={r.match_id} className="text-white">
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* My Data Toggle */}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"}`}>
                  <Switch
                    id="include-user-data-mobile"
                    checked={includeUserData}
                    onCheckedChange={handleUserDataToggle}
                    disabled={preparingContext}
                    className={isCyber ? "data-[state=checked]:bg-cyan-500" : "data-[state=checked]:bg-divine-gold"}
                  />
                  <Label htmlFor="include-user-data-mobile" className="text-white/60 text-sm cursor-pointer whitespace-nowrap">
                    My Data
                  </Label>
                </div>
              </div>
              
              {/* Row 3: Scenario (only when Practice mode) */}
              {chatType === "practice" && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-emerald-400 text-xs">🎯</span>
                    <span className="text-white/40 text-xs">Scenario</span>
                  </div>
                  <Select
                    value={selectedScenario || "none"}
                    onValueChange={(value) => setSelectedScenario(value === "none" ? null : value)}
                  >
                    <SelectTrigger className="w-full border-emerald-500/30 bg-emerald-500/10">
                      <SelectValue placeholder="Select Scenario" />
                    </SelectTrigger>
                    <SelectContent className={isCyber ? "bg-[#0a0a0f] border-emerald-500/30" : "bg-divine-black border-emerald-500/30"}>
                      <SelectItem value="none" className="text-white/60">No Scenario</SelectItem>
                      {sortedScenarios.map((scenario) => {
                        const diffColors = { easy: "text-green-400", medium: "text-yellow-400", hard: "text-red-400" };
                        const diffIcons = { easy: "⚡", medium: "🌱", hard: "🔥" };
                        return (
                          <SelectItem key={scenario.scenario_id} value={scenario.scenario_id} className="text-white">
                            <span className="flex items-center gap-2">
                              <span className={diffColors[scenario.difficulty]}>{diffIcons[scenario.difficulty]}</span>
                              {scenario.name}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Button
              onClick={createNewSession}
              className={`w-full mb-6 ${
                isCyber 
                  ? "bg-cyan-500 hover:bg-cyan-400 text-black" 
                  : "bg-divine-gold hover:bg-divine-gold-light text-black"
              }`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Start New Test Session
            </Button>

            {loadingSessions ? (
              <div className="text-center py-8">
                <div className={`w-8 h-8 border-2 ${isCyber ? "border-cyan-500" : "border-divine-gold"} border-t-transparent rounded-full animate-spin mx-auto`}></div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <Settings className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">No test sessions yet</p>
                <p className="text-white/30 text-sm mt-2">Start a new session to begin testing</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-white/40 text-sm mb-3">Previous Sessions</p>
                {sessions.map((session) => {
                  // Get session type info
                  const sessionType = session.mode || "coaching";
                  const typeConfig = {
                    direct: { icon: "⚡", label: "Direct", color: "orange", border: "border-orange-500/30", bg: "bg-orange-500/10", text: "text-orange-400" },
                    coaching: { icon: "💬", label: "Coaching", color: "cyan", border: "border-cyan-500/30", bg: "bg-cyan-500/10", text: "text-cyan-400" },
                    persona: { icon: "👤", label: "Persona", color: "purple", border: "border-purple-500/30", bg: "bg-purple-500/10", text: "text-purple-400" },
                    practice: { icon: "🎯", label: "Practice", color: "emerald", border: "border-emerald-500/30", bg: "bg-emerald-500/10", text: "text-emerald-400" },
                    function: { icon: "🔧", label: "Function", color: "pink", border: "border-pink-500/30", bg: "bg-pink-500/10", text: "text-pink-400" }
                  };
                  const config = typeConfig[sessionType] || typeConfig.coaching;
                  
                  // Get persona name if available
                  const personaName = session.match_id 
                    ? relationships.find(r => r.match_id === session.match_id)?.name 
                    : null;
                  
                  // Get scenario name if available
                  const scenarioName = session.scenario_id
                    ? practiceScenarios.find(s => s.scenario_id === session.scenario_id)?.name
                    : null;
                  
                  // Parse model info - check direct fields first, then fallback to custom_system_prompt
                  let modelInfo = null;
                  if (session.model_name) {
                    modelInfo = session.model_name;
                  } else if (session.custom_system_prompt) {
                    try {
                      const parsed = JSON.parse(session.custom_system_prompt);
                      if (parsed.model_name) {
                        modelInfo = parsed.model_name;
                      }
                    } catch (e) { console.debug("session model_info parse failed:", e?.message || e); }
                  }
                  
                  return (
                    <div
                      key={session.session_id}
                      onClick={() => loadSession(session.session_id)}
                      className={`p-4 rounded-xl cursor-pointer transition-all group ${config.bg} ${config.border} border hover:brightness-125`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Title row with type badge */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}>
                              {config.icon} {config.label}
                            </span>
                            {session.is_favourite && <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />}
                            {modelInfo && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/60">
                                {modelInfo}
                              </span>
                            )}
                          </div>
                          
                          {/* Session title */}
                          <h3 className="text-white font-medium truncate">{session.title}</h3>
                          
                          {/* Details row */}
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-white/50">
                            <span>{new Date(session.updated_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span>{session.message_count || 0} messages</span>
                            {personaName && (
                              <>
                                <span>•</span>
                                <span className="text-purple-400">👤 {personaName}</span>
                              </>
                            )}
                            {scenarioName && (
                              <>
                                <span>•</span>
                                <span className="text-emerald-400">🎯 {scenarioName}</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Icons */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0">
                          <button
                            onClick={(e) => renameSession(session.session_id, e)}
                            className="p-1.5 hover:bg-white/10 rounded-lg transition-all"
                            title="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5 text-white/60 hover:text-white" />
                          </button>
                          <button
                            onClick={(e) => toggleFavourite(session.session_id, e)}
                            className="p-1.5 hover:bg-yellow-500/20 rounded-lg transition-all"
                            title={session.is_favourite ? "Remove from favourites" : "Add to favourites"}
                          >
                            <Star className={`w-3.5 h-3.5 ${session.is_favourite ? "text-yellow-400 fill-yellow-400" : "text-white/60 hover:text-yellow-400"}`} />
                          </button>
                          <button
                            onClick={(e) => deleteSession(session.session_id, e)}
                            className="p-1.5 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-white/60 hover:text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Chat Messages - Scrollable area with padding for fixed header/footer */}
          <div className="flex-1 overflow-y-auto pt-20 pb-36">
            <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
              {/* Back to sessions */}
              {currentSessionId && (
                <div className="mb-4">
                  <button
                    onClick={() => {
                      setShowSessionList(true);
                      setCurrentSessionId(null);
                      setMessages([]);
                    }}
                    className={`text-sm flex items-center gap-1 ${isCyber ? "text-cyan-400" : "text-divine-gold"} hover:underline`}
                  >
                    <ArrowLeft className="w-3 h-3" />
                    All Sessions
                  </button>
                </div>
              )}

              {messages.length === 0 && (
                <div className="text-center py-16">
                  <Sparkles className={`w-12 h-12 mx-auto mb-4 ${isCyber ? "text-cyan-500/30" : "text-divine-gold/30"}`} />
                  <p className="text-white/40">Start testing with any message</p>
                  <div className="text-white/30 text-sm mt-4 space-y-1">
                    <p><code className="bg-white/10 px-1 rounded">/prompt</code> - View status & all commands</p>
                    <p><code className="bg-white/10 px-1 rounded">/SSP [prompt]</code> - Set custom system prompt</p>
                    <p><code className="bg-white/10 px-1 rounded">/cc</code> - Enable copyable text blocks</p>
                    <p><code className="bg-white/10 px-1 rounded">/medump</code> / <code className="bg-white/10 px-1 rounded">/mdf</code> - Your data (summary/full)</p>
                    <p><code className="bg-white/10 px-1 rounded">/youdump</code> / <code className="bg-white/10 px-1 rounded">/ydf</code> - Persona data (summary/full)</p>
                    <p><code className="bg-white/10 px-1 rounded">/flirt mode</code> - Toggle flirt mode</p>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {message.role !== "user" && (
                    <div className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 ${
                      message.isError ? "bg-red-500/20" : message.isAmp ? (isCyber ? "bg-purple-500/20" : "bg-amber-500/20") : isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"
                    }`}>
                      {message.isAmp ? (
                        <Sparkles className={`w-5 h-5 ${isCyber ? "text-purple-400" : "text-amber-400"}`} />
                      ) : (
                        <Bot className={`w-5 h-5 ${message.isError ? "text-red-400" : isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
                      )}
                    </div>
                  )}
                  
                  <div className={`${message.isAmp ? "max-w-[95%] w-full" : "max-w-full sm:max-w-[80%]"} ${message.role === "user" ? "order-1" : ""}`}>
                    {message.isAmp && message.ampResults ? (
                      <div className={`rounded-2xl px-5 py-4 ${
                        isCyber 
                          ? "bg-white/5 border border-purple-500/30" 
                          : "bg-white/5 border border-amber-500/30"
                      }`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className={`w-4 h-4 ${isCyber ? "text-purple-400" : "text-amber-400"}`} />
                          <span className={`font-semibold ${isCyber ? "text-purple-400" : "text-amber-400"}`}>
                            All Models Parallel Comparison
                          </span>
                        </div>
                        <AmpResults results={message.ampResults} prompt={message.ampPrompt} isCyber={isCyber} />
                      </div>
                    ) : (
                      <>
                        <div className={`inline-block rounded-2xl px-5 py-3 ${
                          message.role === "user"
                            ? isCyber 
                              ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white"
                              : "bg-divine-gold text-black"
                            : message.isCommand
                              ? "bg-white/5 border border-white/10 text-white/80"
                              : message.isError
                                ? "bg-red-500/10 border border-red-500/30 text-white"
                                : isCyber
                                  ? "bg-white/5 border border-cyan-500/20 text-white"
                                  : "bg-white/5 border border-divine-gold/20 text-white"
                        }`}>
                          <MessageContent content={message.content} isCyber={isCyber} />
                        </div>
                        <div className="flex items-center gap-2 mt-1 px-2">
                          {message.model && (
                            <p className="text-white/30 text-xs">{message.model}</p>
                          )}
                          <button
                            onClick={async () => {
                              // Get rendered text by creating a temp element
                              const temp = document.createElement('div');
                              temp.innerHTML = DOMPurify.sanitize(message.content
                                .replace(/\*\*(.*?)\*\*/g, '$1')
                                .replace(/\*(.*?)\*/g, '$1')
                                .replace(/`(.*?)`/g, '$1')
                                .replace(/\n/g, '\n'));
                              const text = temp.textContent || temp.innerText || message.content;
                              try {
                                await navigator.clipboard.writeText(text);
                                toast.success("Copied to clipboard");
                              } catch (err) {
                                toast.error("Failed to copy");
                              }
                            }}
                            className={`p-1 rounded hover:bg-white/10 transition-colors ${
                              isCyber ? "text-white/30 hover:text-cyan-400" : "text-white/30 hover:text-divine-gold"
                            }`}
                            title="Copy message"
                            data-testid={`copy-message-${index}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        
                        {/* Practice Feedback Panel */}
                        {message.practiceFeedback && !message.practiceFeedback.error && (
                          <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-emerald-400 font-medium text-sm">🎯 Practice Feedback</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                message.practiceFeedback.overall_score >= 70 ? "bg-green-500/20 text-green-400" :
                                message.practiceFeedback.overall_score >= 40 ? "bg-yellow-500/20 text-yellow-400" :
                                "bg-red-500/20 text-red-400"
                              }`}>
                                Score: {message.practiceFeedback.overall_score}/100
                              </span>
                            </div>
                            
                            {/* Specific Feedback */}
                            <p className="text-white/70 text-sm mb-2">{message.practiceFeedback.specific_feedback}</p>
                            
                            {/* Strengths */}
                            {message.practiceFeedback.strengths?.length > 0 && (
                              <div className="mb-2">
                                <span className="text-green-400 text-xs font-medium">✓ Strengths:</span>
                                <ul className="text-white/60 text-xs ml-4 mt-1">
                                  {message.practiceFeedback.strengths.map((s, i) => (
                                    <li key={`strength-${i}-${s.slice(0,20)}`}>• {s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Areas to Improve */}
                            {message.practiceFeedback.areas_to_improve?.length > 0 && (
                              <div className="mb-2">
                                <span className="text-yellow-400 text-xs font-medium">△ Areas to Improve:</span>
                                <ul className="text-white/60 text-xs ml-4 mt-1">
                                  {message.practiceFeedback.areas_to_improve.map((a, i) => (
                                    <li key={`improve-${i}-${a.slice(0,20)}`}>• {a}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Alternative Approaches */}
                            {message.practiceFeedback.alternative_approaches?.length > 0 && (
                              <div className="mb-2">
                                <span className="text-cyan-400 text-xs font-medium">💡 Try Instead:</span>
                                <ul className="text-white/60 text-xs ml-4 mt-1">
                                  {message.practiceFeedback.alternative_approaches.map((alt, i) => (
                                    <li key={`alt-${i}-${alt.slice(0,20)}`} className="italic">&ldquo;{alt}&rdquo;</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Next Step */}
                            {message.practiceFeedback.next_step_suggestion && (
                              <div className="mt-2 pt-2 border-t border-emerald-500/20">
                                <span className="text-emerald-400 text-xs">→ Next: </span>
                                <span className="text-white/60 text-xs">{message.practiceFeedback.next_step_suggestion}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  {message.role === "user" && (
                    <div className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 ${
                      isCyber ? "bg-purple-500/20" : "bg-divine-gold/20"
                    }`}>
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <User className={`w-5 h-5 ${isCyber ? "text-purple-400" : "text-divine-gold"}`} />
                      )}
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex gap-4">
                  <div className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center ${isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"}`}>
                    <Bot className={`w-5 h-5 ${isCyber ? "text-cyan-400" : "text-divine-gold"} animate-pulse`} />
                  </div>
                  <div className={`rounded-2xl px-5 py-3 ${isCyber ? "bg-white/5 border border-cyan-500/20" : "bg-white/5 border border-divine-gold/20"}`}>
                    <div className="flex gap-2">
                      <div className={`w-2 h-2 ${isCyber ? "bg-cyan-400" : "bg-divine-gold"} rounded-full animate-bounce`} />
                      <div className={`w-2 h-2 ${isCyber ? "bg-cyan-400" : "bg-divine-gold"} rounded-full animate-bounce`} style={{ animationDelay: "0.1s" }} />
                      <div className={`w-2 h-2 ${isCyber ? "bg-cyan-400" : "bg-divine-gold"} rounded-full animate-bounce`} style={{ animationDelay: "0.2s" }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area - Fixed at bottom */}
          <div className={`fixed bottom-0 left-0 right-0 z-50 border-t ${isCyber ? "border-cyan-500/30 bg-[#0a0a0f]" : "border-divine-gold/30 bg-divine-black"}`}>
            <div className="max-w-4xl mx-auto px-6 py-4">
              <ChatInput 
                onSend={handleSendMessage} 
                loading={loading} 
                isCyber={isCyber} 
              />
              <p className="text-white/30 text-xs mt-2 text-center">
                <code className={`px-1 rounded ${isCyber ? "bg-purple-500/20 text-purple-300" : "bg-amber-500/20 text-amber-300"}`}>/AMP</code> all models • 
                <code className="bg-white/10 px-1 rounded mx-1">/prompt</code> status • 
                <code className="bg-white/10 px-1 rounded">/SSP</code> set prompt • 
                <code className={`px-1 rounded mx-1 ${isCyber ? "bg-cyan-500/20 text-cyan-300" : "bg-divine-gold/20 text-divine-gold"}`}>/cc</code> copy blocks • 
                <code className="bg-white/10 px-1 rounded">/medump</code> <code className="bg-white/10 px-1 rounded">/youdump</code> data • 
                <code className="bg-white/10 px-1 rounded mx-1">/clear</code>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TestChat;
